/**
 * Things the Bible gives a measurement for.
 *
 * Scripture is unusually specific about size — the ark, the temple, Goliath,
 * Og's bed — and almost nobody pictures any of it correctly. People imagine
 * the ark of the covenant as furniture and it is under four feet long; they
 * imagine Goliath as a tall man and he is nearly ten feet. The numbers are
 * right there in the text and the surprise survives knowing the story, which
 * is what makes this worth playing rather than reading.
 *
 * ── About the cubit ──────────────────────────────────────────────────────────
 *
 * Scripture never defines it. A cubit is the forearm, elbow to fingertip, and
 * estimates for the common Hebrew cubit run from roughly 17½ to 18½ inches;
 * the royal cubit was longer again. Every figure here uses 18in, the standard
 * estimate, and the game says so on the results screen rather than presenting
 * a converted number as though it were exact. Getting this wrong in the
 * player's favour would be easy and would make the game a liar.
 *
 * Deuteronomy 3:11 is the one place the text hedges for us — "after the cubit
 * of a man" — which is the writer making the same conversion we are.
 *
 * All quoted text is the World English Bible, verbatim, public domain.
 */

/** 18 inches, the standard estimate. Scripture does not define it. */
export const CUBIT_FT = 1.5;
/** A span is half a cubit — the hand spread, thumb tip to little finger. */
export const SPAN_FT = 0.75;

/**
 * `dim` says which dimension the question asks for, because a boat is
 * interesting for its length and a giant for his height, and the drawing has
 * to know which number it was handed.
 *
 * `min`/`max` bound the slider and `start` is where it opens.
 *
 * Two ways a slider hands the answer over, and both were happening. The bounds
 * must not put the truth near an end stop — a slider that bottoms out on the
 * right answer gives it away, and one that cannot reach it is a cheat. And the
 * opening position must not sit on the answer: six of these used to open
 * within 1.5x of the truth, the temple within 11% and the altar within 7%, so
 * locking in without touching anything scored nearly full marks.
 *
 * Every start is now at least 1.7x away, alternating over and under so there
 * is no "it always starts low" to learn instead.
 */
export const MEASURES = [
  {
    id: 'goliath',
    q: 'How tall was Goliath?',
    shape: 'warrior',
    dim: 'height',
    truth: 6 * CUBIT_FT + SPAN_FT,
    maths: '6 cubits and a span · 6 × 18in + 9in',
    verse:
      'A champion out of the camp of the Philistines named Goliath, of Gath, whose height was six cubits and a span went out.',
    ref: '1 Samuel 17:4',
    min: 5,
    max: 30,
    start: 22,
  },
  {
    id: 'ark',
    q: 'How long was Noah’s ark?',
    shape: 'ark',
    dim: 'length',
    truth: 300 * CUBIT_FT,
    maths: '300 cubits long · 50 wide · 30 high',
    verse:
      'This is how you shall make it. The length of the ship shall be three hundred cubits, its width fifty cubits, and its height thirty cubits.',
    ref: 'Genesis 6:15',
    min: 40,
    max: 1400,
    start: 90,
  },
  {
    id: 'image',
    q: 'How tall was the golden image?',
    shape: 'idol',
    dim: 'height',
    truth: 60 * CUBIT_FT,
    maths: '60 cubits high · 6 cubits wide — ten times taller than it was broad',
    verse:
      'Nebuchadnezzar the king made an image of gold, whose height was sixty cubits, and its width six cubits. He set it up in the plain of Dura, in the province of Babylon.',
    ref: 'Daniel 3:1',
    min: 8,
    max: 400,
    start: 300,
  },
  {
    id: 'temple',
    q: 'How long was Solomon’s temple?',
    shape: 'temple',
    dim: 'length',
    truth: 60 * CUBIT_FT,
    maths: '60 cubits long · 20 wide · 30 high',
    verse:
      'The house which king Solomon built for Yahweh had a length of sixty cubits, and its width twenty, and its height thirty cubits.',
    ref: '1 Kings 6:2',
    min: 15,
    max: 800,
    start: 25,
  },
  {
    id: 'covenant',
    q: 'How long was the ark of the covenant?',
    shape: 'chest',
    dim: 'length',
    truth: 2.5 * CUBIT_FT,
    maths: '2½ cubits long · 1½ wide · 1½ high',
    verse:
      '“They shall make an ark of acacia wood. Its length shall be two and a half cubits, its width a cubit and a half, and a cubit and a half its height.',
    ref: 'Exodus 25:10',
    min: 1,
    max: 24,
    start: 14,
  },
  {
    id: 'ogbed',
    q: 'How long was Og’s iron bed?',
    shape: 'chest',
    dim: 'length',
    truth: 9 * CUBIT_FT,
    maths: '9 cubits long · 4 cubits wide — “after the cubit of a man”',
    verse:
      'For only Og king of Bashan remained of the remnant of the Rephaim. Behold, his bedstead was a bedstead of iron… Nine cubits was its length, and four cubits its width, after the cubit of a man.',
    ref: 'Deuteronomy 3:11',
    min: 3,
    max: 60,
    start: 4.5,
  },
  {
    id: 'sea',
    q: 'How wide was the molten sea?',
    shape: 'basin',
    dim: 'length',
    truth: 10 * CUBIT_FT,
    maths: '10 cubits brim to brim · 5 high · a line of 30 cubits round it',
    verse:
      'He made the molten sea of ten cubits from brim to brim, round in shape. Its height was five cubits; and a line of thirty cubits encircled it.',
    ref: '1 Kings 7:23',
    min: 3,
    max: 80,
    start: 45,
  },
  {
    id: 'pillars',
    q: 'How tall were the bronze pillars?',
    shape: 'idol',
    dim: 'height',
    truth: 18 * CUBIT_FT,
    maths: '18 cubits high · a line of 12 cubits round each',
    verse:
      'For he fashioned the two pillars of bronze, eighteen cubits high apiece; and a line of twelve cubits encircled either of them.',
    ref: '1 Kings 7:15',
    min: 5,
    max: 150,
    start: 8,
  },
  {
    id: 'holiest',
    q: 'How wide was the Holy of Holies?',
    shape: 'temple',
    dim: 'length',
    truth: 20 * CUBIT_FT,
    maths: '20 cubits long, wide and high — a perfect cube, overlaid with gold',
    verse:
      'Within the inner sanctuary was twenty cubits in length, and twenty cubits in width, and twenty cubits in its height; and he overlaid it with pure gold; and he covered the altar with cedar.',
    ref: '1 Kings 6:20',
    min: 6,
    max: 200,
    start: 95,
  },
  {
    id: 'altar',
    q: 'How wide was the altar?',
    shape: 'chest',
    dim: 'length',
    truth: 5 * CUBIT_FT,
    maths: '5 cubits long · 5 wide · 3 high — square',
    verse:
      '“You shall make the altar of acacia wood, five cubits long, and five cubits wide. The altar shall be square. Its height shall be three cubits.',
    ref: 'Exodus 27:1',
    min: 2,
    max: 50,
    start: 24,
  },
];

/** How many go into one run. Five is a session, not a homework assignment. */
export const PER_RUN = 5;
